import { useState } from "react";
import type { Api } from "../../shared/api";
import type { TurnView, UsageView } from "../../shared/contracts";
import { USAGE_CATEGORY_LABELS, type UsageBreakdown, type UsageCategoryTotals } from "../../shared/usage";
import { IconCheck, IconX } from "../components/icons";
import { SubagentsPanel } from "../components/subagents-panel";
import { Badge, Card, cx, ErrorNotice, Loading, PageTop } from "../components/ui";
import { api } from "../lib/api";
import { fmtDateTime, fmtDuration, fmtNumber, fmtUsd, toolLabel, triggerLabel, TURN_STATUS_LABEL } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { useResource } from "../lib/store";

type Activity = Api["GET /api/activity"]["res"];

const STATUS_TONE: Record<TurnView["status"], "neutral" | "accent" | "success" | "danger"> = {
  queued: "neutral",
  running: "accent",
  done: "success",
  failed: "danger",
  cancelled: "neutral",
};

/** /activite — what the agent did (each turn: trigger, status, cost, tools) and the month's spending. */
export function ActivityPage() {
  usePageTitle("Activité");
  const now = useNow(30_000);
  const activity = useResource<Activity>("activity", (signal) => api("GET /api/activity", { signal }), {
    refreshOn: ["turn.done", "turn.started"],
  });

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-[18px]">
      <PageTop title="Activité" back="/reglages" className="mb-0" />
      {activity.loading ? (
        <Loading />
      ) : !activity.data ? (
        <ErrorNotice message={activity.error?.message ?? "Impossible de charger l'activité."} onRetry={() => void activity.reload()} />
      ) : (
        <>
          <UsageCard usage={activity.data.usage} />
          <SpendCard />
          <SubagentsPanel now={now} />
          <section aria-labelledby="activity-turns" className="flex flex-col gap-2">
            <h2 id="activity-turns" className="px-1 text-[13px] font-semibold text-subtle">
              Derniers tours
            </h2>
            {activity.data.turns.length ? (
              <ul className="flex flex-col gap-1.5">
                {activity.data.turns.map((t) => (
                  <TurnRow key={t.id} turn={t} now={now} />
                ))}
              </ul>
            ) : (
              <p className="px-1 py-6 text-[14px] text-muted">Rien pour l'instant</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function UsageCard({ usage }: { usage: UsageView }) {
  const budget = usage.userBudgetUsd;
  const ratio = budget > 0 ? Math.min(1, usage.monthUsd / budget) : 0;
  const tone = ratio >= 0.9 ? "bg-danger" : ratio >= 0.7 ? "bg-warning" : "bg-fg";
  return (
    <Card className="p-4 sm:p-5">
      <h2 className="text-[13px] font-semibold text-subtle">Mois en cours</h2>
      <p className="mt-2 text-[26px] font-semibold tracking-tight tabular-nums">
        {fmtUsd(usage.monthUsd)}
        {budget > 0 ? <span className="text-[15px] font-normal text-muted"> / {fmtUsd(budget)} de budget</span> : null}
      </p>
      {budget > 0 ? (
        <div
          className="mt-3 h-1.5 overflow-hidden rounded-full bg-[var(--track)]"
          role="progressbar"
          aria-label="Budget du mois utilisé"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(ratio * 100)}
        >
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
        </div>
      ) : null}
      <p className="mt-2 text-[12.5px] text-subtle">Aujourd'hui : {fmtUsd(usage.todayUsd)}</p>
      {usage.globalBudgetUsd !== undefined && usage.globalMonthUsd !== undefined ? (
        <p className="mt-1 text-[12.5px] text-subtle">
          Tous les comptes : {fmtUsd(usage.globalMonthUsd)} / {fmtUsd(usage.globalBudgetUsd)}
        </p>
      ) : null}
    </Card>
  );
}

/** « Dépenses » — what today and the last 7 days cost, by kind of work (GET /api/usage/breakdown). */
function SpendCard() {
  const spend = useResource<UsageBreakdown>("usage-breakdown", (signal) => api("GET /api/usage/breakdown", { signal }), { refreshOn: ["turn.done"] });
  const [period, setPeriod] = useState<"today" | "week">("today");
  if (!spend.data) return null;
  const data = spend.data;
  const rows = period === "today" ? data.todayByCategory : data.last7ByCategory;
  const total = period === "today" ? data.todayUsd : data.last7Usd;
  const max = Math.max(...data.days.map((d) => d.usd), data.dailyTargetUsd, 0.0001);
  return (
    <Card className="p-4 sm:p-5" data-testid="spend-card">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="text-[13px] font-semibold text-subtle">Dépenses</h2>
        <div className="ml-auto flex gap-1 rounded-full bg-bg p-0.5" role="tablist" aria-label="Période">
          {(["today", "week"] as const).map((p) => (
            <button
              key={p}
              type="button"
              role="tab"
              aria-selected={period === p}
              onClick={() => setPeriod(p)}
              className={cx("rounded-full px-3 py-1 text-[12.5px] font-semibold transition-colors", period === p ? "bg-bubble-user text-fg" : "text-muted hover:text-fg")}
            >
              {p === "today" ? "Aujourd'hui" : "7 jours"}
            </button>
          ))}
        </div>
      </div>
      <p className="mt-2 text-[22px] font-semibold tracking-tight tabular-nums" data-testid="spend-total">
        {fmtUsd(total)}
        {period === "today" && data.dailyTargetUsd > 0 ? <span className="text-[13px] font-normal text-muted"> · objectif {fmtUsd(data.dailyTargetUsd)} par jour</span> : null}
      </p>
      <div className="mt-3 flex h-12 items-end gap-1" aria-label="Dépenses des 7 derniers jours">
        {data.days.map((d) => (
          <div
            key={d.day}
            title={`${d.day} : ${fmtUsd(d.usd)}`}
            className={cx("flex-1 rounded-t", d.usd > data.dailyTargetUsd && data.dailyTargetUsd > 0 ? "bg-warning" : "bg-muted")}
            style={{ height: `${Math.max(4, Math.round((d.usd / max) * 100))}%` }}
          />
        ))}
      </div>
      {rows.length ? (
        <ul className="mt-4 space-y-1.5">
          {rows.map((r) => (
            <SpendRow key={r.category} row={r} total={total} />
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-[12.5px] text-muted">Rien de dépensé sur cette période.</p>
      )}
      {period === "today" && data.todayCacheHitRatio > 0 ? (
        <p className="mt-3 text-[12px] text-subtle">Relu depuis le cache : {Math.round(data.todayCacheHitRatio * 100)} % du contexte envoyé aujourd'hui.</p>
      ) : null}
    </Card>
  );
}

function SpendRow({ row, total }: { row: UsageCategoryTotals; total: number }) {
  const share = total > 0 ? Math.round((row.usd / total) * 100) : 0;
  const tokens = row.inputTokens + row.cacheWriteTokens + row.cacheReadTokens + row.outputTokens;
  return (
    <li data-testid="spend-row" data-category={row.category} className="flex items-center gap-2 text-[13px]">
      <span className="min-w-0 flex-1 truncate">{USAGE_CATEGORY_LABELS[row.category]}</span>
      <span className="text-[11.5px] text-subtle tabular-nums">
        {row.calls} appel{row.calls > 1 ? "s" : ""}
        {tokens > 0 ? ` · ${fmtNumber(tokens)} jetons` : ""}
      </span>
      <span className="w-20 text-right tabular-nums">{fmtUsd(row.usd)}</span>
      <span className="w-9 text-right text-[11.5px] text-muted tabular-nums">{share} %</span>
    </li>
  );
}

function TurnRow({ turn, now }: { turn: TurnView; now: number }) {
  const duration = turn.startedAt && turn.finishedAt ? fmtDuration(Math.max(0, turn.finishedAt - turn.startedAt)) : null;
  return (
    <li data-testid="turn-row" className="min-w-0 rounded-2xl bg-elev px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[14px] font-medium">{triggerLabel(turn.trigger)}</span>
        <Badge tone={STATUS_TONE[turn.status] ?? "neutral"}>{TURN_STATUS_LABEL[turn.status] ?? turn.status}</Badge>
        <span className="ml-auto text-[12.5px] text-muted tabular-nums">{fmtUsd(turn.costUsd)}</span>
      </div>
      <p className="mt-0.5 text-[12px] text-subtle">
        {fmtDateTime(turn.createdAt, now)}
        {duration ? ` · ${duration}` : ""}
      </p>
      {turn.tools.length ? (
        <ul className="mt-2 flex flex-wrap gap-1.5">
          {turn.tools.map((tool, i) => (
            <li key={`${i}-${tool.name}`} className="inline-flex items-center gap-1 rounded-full bg-chip px-2 py-0.5 text-[11.5px] text-muted">
              {tool.ok ? <IconCheck width={11} height={11} className="text-success" /> : <IconX width={11} height={11} className="text-danger" />}
              {toolLabel(tool.name)}
            </li>
          ))}
        </ul>
      ) : null}
      {turn.error ? <p className="mt-2 text-[12.5px] text-danger wrap-anywhere">{turn.error}</p> : null}
    </li>
  );
}
